/**
 * V2 Tab Metadata — maps every staff-facing tab ID to breadcrumb + title
 * Used by V2LayoutShell to auto-wrap pages with V2PageChrome.
 */

interface TabMeta {
  crumb: string
  title: string
  flush?: boolean
}

export const V2_TAB_META: Record<string, TabMeta> = {
  // ── Today ─────────────────────────────────────────
  'home': { crumb: 'Today', title: 'Home', flush: true },
  // 'inbox' — retired (TRUS-709): redirects to workspace-messaging
  'workspace-messaging': { crumb: 'Today', title: 'Messaging', flush: true },

  // ── Work ──────────────────────────────────────────
  'members': { crumb: 'Work', title: 'Members & cases', flush: true },
  // 'workspace' — EXCLUDED: CaseWorkspace renders its own header
  'members-new': { crumb: 'Work', title: 'New member' },
  'members-edit': { crumb: 'Work', title: 'Edit member' },
  'members-view': { crumb: 'Work', title: 'Member profile', flush: true },
  'contacts': { crumb: 'Work', title: 'Contacts', flush: true },
  'contacts-new': { crumb: 'Work', title: 'New contact' },
  'contacts-view': { crumb: 'Work', title: 'Contact detail', flush: true },
  'contacts-edit': { crumb: 'Work', title: 'Edit contact' },
  'household-view': { crumb: 'Work', title: 'Household', flush: true },
  'enrollments-view': { crumb: 'Work', title: 'Enrollment detail', flush: true },
  // 'workspace' — EXCLUDED: CaseWorkspace renders its own header
  'queue': { crumb: 'Work', title: 'Referral queue', flush: true },
  'create-referral': { crumb: 'Work', title: 'New referral' },
  'unmatched': { crumb: 'Work', title: 'Unmatched referrals', flush: true },
  'matching': { crumb: 'Work', title: 'Auto-match', flush: true },
  'finder': { crumb: 'Work', title: 'Candidate finder', flush: true },
  'livefeed': { crumb: 'Work', title: 'Live feed', flush: true },
  // 'calendar' — EXCLUDED: CalendarPage renders its own header
  'telehealth': { crumb: 'Work', title: 'Telehealth', flush: true },
  'telehealth-waiting': { crumb: 'Work', title: 'Waiting room', flush: true },
  'telehealth-session': { crumb: 'Work', title: 'Telehealth session', flush: true },
  'telehealth-encounters': { crumb: 'Work', title: 'Encounters', flush: true },
  'telehealth-by-member': { crumb: 'Work', title: 'Member telehealth', flush: true },
  'telehealth-reports': { crumb: 'Work', title: 'Telehealth reports', flush: true },
  'form-library': { crumb: 'Work', title: 'Forms & consents', flush: true },
  'form-templates': { crumb: 'Work', title: 'Form builder' },
  'form-analytics': { crumb: 'Work', title: 'Form analytics', flush: true },
  'workflow-instances': { crumb: 'Work', title: 'Workflow center', flush: true },
  'workflows': { crumb: 'Work', title: 'Workflow templates', flush: true },
  'automation-rules': { crumb: 'Work', title: 'Automation rules', flush: true },

  // ── Network ───────────────────────────────────────
  'directory': { crumb: 'Network', title: 'My providers', flush: true },
  'provider-directory': { crumb: 'Network', title: 'Provider directory', flush: true },
  'provider-communications': { crumb: 'Network', title: 'Provider communications', flush: true },

  // ── Communications ────────────────────────────────
  'approvals': { crumb: 'Communications', title: 'Approvals', flush: true },
  'groups': { crumb: 'Communications', title: 'Groups', flush: true },
  'bulk-communications': { crumb: 'Communications', title: 'Engagements', flush: true },
  'campaigns': { crumb: 'Communications', title: 'Campaigns', flush: true },
  'chatflows': { crumb: 'Communications', title: 'Chatflows', flush: true },
  'interactive-content': { crumb: 'Communications', title: 'Surveys & polls', flush: true },
  'media-library': { crumb: 'Communications', title: 'Media library', flush: true },
  'communication-history': { crumb: 'Communications', title: 'History', flush: true },

  // ── Dashboards ────────────────────────────────────
  'member-retention': { crumb: 'Dashboard', title: 'Retention intelligence', flush: true },
  'sponsor-retention': { crumb: 'Dashboard', title: 'Network retention', flush: true },

  // ── Community Programs ────────────────────────────
  'grant-calendar': { crumb: 'Community', title: 'Grant calendar', flush: true },
  'donor-pipeline': { crumb: 'Community', title: 'Donor pipeline', flush: true },
  'donations': { crumb: 'Community', title: 'Donations', flush: true },
  'donor-events': { crumb: 'Community', title: 'Donor events', flush: true },
  'donor-cohorts': { crumb: 'Community', title: 'Donor cohorts', flush: true },
  'fundraising-home': { crumb: 'Community', title: 'Fundraising', flush: true },
  'mutual-aid': { crumb: 'Community', title: 'Mutual aid & pods', flush: true },

  // ── Eligibility ───────────────────────────────────
  'eligibility-screening': { crumb: 'Eligibility', title: 'Pre-screening' },
  'eligibility-verification': { crumb: 'Eligibility', title: 'Verification' },
  'eligibility-direct': { crumb: 'Eligibility', title: 'Direct verification' },
  'eligibility-credentials': { crumb: 'Eligibility', title: 'Credentials' },
  // 'ffs-transition-cohort' — EXCLUDED: page renders its own PageHeader (TRUS-724)
  // 'verify' — EXCLUDED: VerifyPage renders its own PageHeader with tabs

  // ── Reports ───────────────────────────────────────
  'analytics': { crumb: 'Reports', title: 'Analytics', flush: true },
  'billing-analytics': { crumb: 'Reports', title: 'Billing analytics', flush: true },
  'case-notes-analysis': { crumb: 'Reports', title: 'Case notes analysis', flush: true },
  'geographic-risk-map': { crumb: 'Reports', title: 'Geographic risk map', flush: true },

  // ── Care Coordination ─────────────────────────────
  'care-coordination-hub': { crumb: 'Care', title: 'Coordination hub', flush: true },

  // ── Community Care Hub (CCH) — grouped pages ─────
  'cch-sponsor-console': { crumb: 'CCH', title: 'Sponsor Console', flush: true },
  'cch-care-management': { crumb: 'CCH', title: 'Care Management', flush: true },
  'cch-hub-admin': { crumb: 'CCH', title: 'Hub Administration', flush: true },
  'cch-reporting': { crumb: 'CCH', title: 'Reporting & Outcomes', flush: true },
  // Individual CCH tabs (still routable from command palette)
  'cch-care-plans': { crumb: 'CCH', title: 'Care Plans', flush: true },
  'cch-authorizations': { crumb: 'CCH', title: 'Service Authorizations', flush: true },
  'cch-quality-measures': { crumb: 'CCH', title: 'Quality Measures', flush: true },
  'cch-funds-flow': { crumb: 'CCH', title: 'Funds Flow Ledger', flush: true },
  'cch-credentialing': { crumb: 'CCH', title: 'Credentialing', flush: true },
  'cch-discharge-planning': { crumb: 'CCH', title: 'Discharge Planning', flush: true },
  'cch-grievances': { crumb: 'CCH', title: 'Grievances & Appeals', flush: true },
  'cch-outcomes': { crumb: 'CCH', title: 'Outcomes Tracking', flush: true },
  'cch-chw-supervision': { crumb: 'CCH', title: 'CHW Supervision', flush: true },
  'cch-nemt': { crumb: 'CCH', title: 'NEMT Transportation', flush: true },
  'cch-housing': { crumb: 'CCH', title: 'Housing & 211', flush: true },
  'cch-accreditation': { crumb: 'CCH', title: 'Accreditation', flush: true },
  'cch-sponsor-console': { crumb: 'CCH', title: 'Sponsor Console', flush: true },
  'service-referrals': { crumb: 'Care', title: 'Service referrals', flush: true },
  'service-referral-workspace': { crumb: 'Care', title: 'Referral workspace', flush: true },

  // ── Settings (user) ───────────────────────────────
  'user-preferences': { crumb: 'Settings', title: 'Preferences' },

  // ── Admin ─────────────────────────────────────────
  'cbo-management': { crumb: 'Admin', title: 'Organizations', flush: true },
  // 'org-settings' — EXCLUDED: OrganizationSettingsPage renders its own header
  'user-management': { crumb: 'Admin', title: 'Users', flush: true },
  // 'organization-roles' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  'feature-capabilities': { crumb: 'Admin', title: 'Capabilities' },
  'page-capabilities': { crumb: 'Admin', title: 'Page access' },
  'role-page-access': { crumb: 'Admin', title: 'Role page access' },
  'nav-layout': { crumb: 'Admin', title: 'Navigation layout' },
  'programs': { crumb: 'Admin', title: 'Programs', flush: true },
  // 'member-fields' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'staff-profiles' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  'filter-chips': { crumb: 'Admin', title: 'Filter chips' },
  'field-mapping': { crumb: 'Admin', title: 'Field mapping' },
  'integrations': { crumb: 'Admin', title: 'Integrations', flush: true },
  'admin': { crumb: 'Admin', title: 'Import manager', flush: true },
  'translation-management': { crumb: 'Admin', title: 'Translation management' },
  'communication-templates': { crumb: 'Admin', title: 'Templates' },
  'campaign-templates': { crumb: 'Admin', title: 'Campaign templates' },
  // 'template-variables' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  'phone-management': { crumb: 'Admin', title: 'Phone management' },
  'data-privacy-settings': { crumb: 'Admin', title: 'Data privacy' },
  'security-compliance': { crumb: 'Admin', title: 'Security & compliance' },
  'break-glass-management': { crumb: 'Admin', title: 'Break glass audit' },
  // 'compliance-documents' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  'document-config': { crumb: 'Admin', title: 'Document management' },
  'hard-copy-tracking': { crumb: 'Admin', title: 'Hard copy tracking', flush: true },
  'client-billing': { crumb: 'Admin', title: 'Billing & payments', flush: true },
  'developer-portal': { crumb: 'Admin', title: 'Developer portal', flush: true },
  'test-runner': { crumb: 'Admin', title: 'Test runner', flush: true },

  // ── Billing ───────────────────────────────────────
  'billing-claims': { crumb: 'Billing', title: 'Claims', flush: true },
  // 'program-billing' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'pmpm-billing' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'clearinghouse-settings' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'mcp-billing-rules' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'billing-ai' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'billing-data-sources' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'billing-ml' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'billing-ml-synthetic-pretrain' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'pdf-templates' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'report-distribution' — EXCLUDED: page renders its own PageHeader (weavein primitives)
  // 'ecm-progress-notes-report' — EXCLUDED: page renders its own PageHeader (weavein primitives)

  // ── Emergency ─────────────────────────────────────
  'emergency-incidents': { crumb: 'Emergency', title: 'Incidents', flush: true },
  'emergency-dashboard': { crumb: 'Emergency', title: 'Situation room', flush: true },
  'emergency-alerts': { crumb: 'Emergency', title: 'Alerts', flush: true },

  // ── Compliance ────────────────────────────────────
  'cbo-compliance-dashboard': { crumb: 'Compliance', title: 'Work compliance', flush: true },
  'ecm-compliance': { crumb: 'Compliance', title: 'ECM compliance', flush: true },
}
